import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Plus, Edit, Trash2, Star, Video, Upload, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to convert YouTube URL to embed format
const convertYouTubeUrl = (url) => {
  if (!url) return '';
  // Don't transform uploaded data URLs
  if (url.startsWith('data:')) return url;
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s?]+)/,
    /youtube\.com\/shorts\/([^&\s?]+)/
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) {
      return `https://www.youtube.com/embed/${match[1]}`;
    }
  }
  return url;
};

const AdminTestimonials = () => {
  const { getAuthHeader } = useAuth();
  const [testimonials, setTestimonials] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    client_name: '',
    company: '',
    profession: '',
    testimonial_type: 'Customer',
    guntha_size: '',
    rating: 5,
    message: '',
    image_url: '',
    video_url: ''
  });

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(`${API}/testimonials`);
      setTestimonials(response.data);
    } catch (error) {
    }
  };

  const handleChange = (e) => {
    const value = e.target.name === 'rating' ? parseInt(e.target.value) : e.target.value;
    setFormData({ ...formData, [e.target.name]: value });
  };

  const openModal = (testimonial = null) => {
    if (testimonial) {
      setEditingTestimonial(testimonial);
      setFormData(testimonial);
    } else {
      setEditingTestimonial(null);
      setFormData({
        client_name: '',
        company: '',
        profession: '',
        testimonial_type: 'Customer',
        guntha_size: '',
        rating: 5,
        message: '',
        image_url: '',
        video_url: ''
      });
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingTestimonial(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Convert YouTube URL to embed format
    const submitData = {
      ...formData,
      video_url: convertYouTubeUrl(formData.video_url)
    };

    try {
      if (editingTestimonial) {
        await axios.put(`${API}/testimonials/${editingTestimonial.id}`, submitData, {
          headers: getAuthHeader()
        });
        toast.success('Testimonial updated successfully!');
      } else {
        await axios.post(`${API}/testimonials`, submitData, {
          headers: getAuthHeader()
        });
        toast.success('Testimonial added successfully!');
      }
      fetchTestimonials();
      closeModal();
    } catch (error) {
      toast.error('Failed to save testimonial');
    }
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('video/')) {
      toast.error('Please select a valid video file');
      return;
    }

    // Limit file size to 10MB (MongoDB doc limit is 16MB; base64 adds ~33% overhead)
    const MAX_SIZE = 10 * 1024 * 1024;
    if (file.size > MAX_SIZE) {
      toast.error('Video must be 10MB or smaller. For larger videos, use the YouTube URL field above.');
      e.target.value = '';
      return;
    }

    setUploading(true);
    try {
      const uploadForm = new FormData();
      uploadForm.append('file', file);
      const response = await axios.post(`${API}/upload-video`, uploadForm, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
      });
      setFormData((prev) => ({ ...prev, video_url: response.data.url }));
      toast.success(`Video uploaded (${response.data.size})`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload video');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this testimonial?')) return;

    try {
      await axios.delete(`${API}/testimonials/${id}`, {
        headers: getAuthHeader()
      });
      toast.success('Testimonial deleted successfully!');
      fetchTestimonials();
    } catch (error) {
      toast.error('Failed to delete testimonial');
    }
  };

  return (
    <div data-testid="admin-testimonials-page">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-4xl font-playfair font-bold text-stone-900 mb-2">Testimonials</h1>
          <p className="text-stone-600">Manage client reviews</p>
        </div>
        <Button
          onClick={() => openModal()}
          data-testid="add-testimonial-button"
          className="bg-emerald-900 hover:bg-emerald-800 text-white px-6 py-3 uppercase tracking-widest text-xs font-bold"
        >
          <Plus className="w-4 h-4 mr-2" /> ADD TESTIMONIAL
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {testimonials.map((testimonial) => (
          <div
            key={testimonial.id}
            data-testid={`testimonial-item-${testimonial.id}`}
            className="bg-white border border-stone-200 p-6"
          >
            <div className="flex items-center gap-1 mb-4">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  className={`w-4 h-4 ${
                    i < testimonial.rating ? 'fill-amber-600 text-amber-600' : 'text-stone-300'
                  }`}
                />
              ))}
            </div>
            <p className="text-stone-700 mb-4 italic line-clamp-3">"{testimonial.message}"</p>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-bold text-stone-900">{testimonial.client_name}</p>
                {testimonial.company && <p className="text-stone-500 text-sm">{testimonial.company}</p>}
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={() => openModal(testimonial)}
                  data-testid={`edit-testimonial-${testimonial.id}`}
                  variant="outline"
                  size="sm"
                >
                  <Edit className="w-4 h-4" />
                </Button>
                <Button
                  onClick={() => handleDelete(testimonial.id)}
                  data-testid={`delete-testimonial-${testimonial.id}`}
                  variant="destructive"
                  size="sm"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {testimonials.length === 0 && (
        <div className="text-center py-20 bg-white border border-stone-200">
          <p className="text-stone-500">No testimonials yet. Add your first testimonial!</p>
        </div>
      )}

      <Dialog open={isModalOpen} onOpenChange={closeModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="testimonial-form-modal">
          <DialogHeader>
            <DialogTitle className="text-2xl font-playfair font-bold">
              {editingTestimonial ? 'Edit Testimonial' : 'Add Testimonial'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              name="client_name"
              placeholder="Client Name *"
              value={formData.client_name}
              onChange={handleChange}
              required
              data-testid="testimonial-name-input"
            />
            
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">Testimonial Type *</label>
              <select
                name="testimonial_type"
                value={formData.testimonial_type}
                onChange={handleChange}
                required
                className="w-full h-12 px-3 border border-stone-300 focus:ring-1 focus:ring-emerald-900 focus:border-emerald-900 rounded"
              >
                <option value="Customer">Customer</option>
                <option value="Channel Partner">Channel Partner</option>
                <option value="Guntha">Guntha</option>
              </select>
            </div>

            {formData.testimonial_type === 'Guntha' ? (
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">Land Size *</label>
                <select
                  name="guntha_size"
                  value={formData.guntha_size}
                  onChange={handleChange}
                  required={formData.testimonial_type === 'Guntha'}
                  className="w-full h-12 px-3 border border-stone-300 focus:ring-1 focus:ring-emerald-900 focus:border-emerald-900 rounded"
                >
                  <option value="">Select Land Size</option>
                  <option value="2 R">2 R</option>
                  <option value="5 R">5 R</option>
                  <option value="10 R">10 R</option>
                  <option value="20 R">20 R</option>
                  <option value="1 Acre">1 Acre</option>
                  <option value="2 Acre">2 Acre</option>
                  <option value="5 Acre">5 Acre</option>
                  <option value="10 Acre">10 Acre</option>
                </select>
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">Profession {formData.testimonial_type !== 'Guntha' && '*'}</label>
                <select
                  name="profession"
                  value={formData.profession}
                  onChange={handleChange}
                  required={formData.testimonial_type !== 'Guntha'}
                  className="w-full h-12 px-3 border border-stone-300 focus:ring-1 focus:ring-emerald-900 focus:border-emerald-900 rounded"
                >
                  <option value="">Select Profession</option>
                  <option value="Ex Army Officer">Ex Army Officer</option>
                  <option value="Farmer">Farmer</option>
                  <option value="Housewife">Housewife</option>
                  <option value="Retired Person">Retired Person</option>
                  <option value="Driver">Driver</option>
                  <option value="Banker">Banker</option>
                  <option value="Police">Police</option>
                  <option value="Teacher">Teacher</option>
                  <option value="Shopkeeper">Shopkeeper</option>
                  <option value="Government Servant">Government Servant</option>
                  <option value="Lawyer">Lawyer</option>
                  <option value="CA">CA</option>
                  <option value="Doctor">Doctor</option>
                  <option value="IT Professional">IT Professional</option>
                  <option value="Architect">Architect</option>
                  <option value="Engineer">Engineer</option>
                  <option value="Investor">Investor</option>
                  <option value="Businessman">Businessman</option>
                  <option value="Cricketer">Cricketer</option>
                  <option value="Actor">Actor</option>
                  <option value="Politician">Politician</option>
                  <option value="Judge">Judge</option>
                  <option value="Commissioner">Commissioner</option>
                  <option value="Deputy Collector">Deputy Collector</option>
                </select>
              </div>
            )}

            <Input
              name="company"
              placeholder="Company (Optional)"
              value={formData.company}
              onChange={handleChange}
              data-testid="testimonial-company-input"
            />
            
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">Rating</label>
              <select
                name="rating"
                value={formData.rating}
                onChange={handleChange}
                required
                data-testid="testimonial-rating-input"
                className="w-full h-12 px-3 border border-stone-300 focus:ring-1 focus:ring-emerald-900 focus:border-emerald-900 rounded"
              >
                {[5, 4, 3, 2, 1].map(num => (
                  <option key={num} value={num}>{num} Stars</option>
                ))}
              </select>
            </div>
            <Textarea
              name="message"
              placeholder="Testimonial Message *"
              value={formData.message}
              onChange={handleChange}
              required
              rows={4}
              data-testid="testimonial-message-input"
            />
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg space-y-3">
              <div>
                <label className="block text-sm font-medium text-blue-800 mb-2">YouTube Video URL (optional)</label>
                <Input
                  name="video_url"
                  placeholder="https://www.youtube.com/watch?v=... or https://youtu.be/..."
                  value={formData.video_url}
                  onChange={handleChange}
                  className="bg-white"
                  data-testid="testimonial-video-input"
                />
                <p className="text-xs text-blue-600 mt-1">Paste any YouTube URL - it will be auto-converted to embed format</p>
              </div>

              <div className="relative flex items-center gap-3">
                <div className="flex-1 h-px bg-blue-200" />
                <span className="text-xs font-semibold text-blue-700 uppercase tracking-widest">OR</span>
                <div className="flex-1 h-px bg-blue-200" />
              </div>

              <div>
                <label className="block text-sm font-medium text-blue-800 mb-2">Upload Video from Computer (optional)</label>
                <div className="flex items-center gap-2">
                  <label
                    htmlFor="testimonial-video-upload"
                    data-testid="testimonial-video-upload-label"
                    className={`flex-1 flex items-center justify-center gap-2 h-12 px-4 rounded border-2 border-dashed cursor-pointer transition-colors ${
                      uploading
                        ? 'border-blue-400 bg-blue-100 text-blue-500 cursor-wait'
                        : 'border-blue-400 bg-white text-blue-700 hover:bg-blue-100'
                    }`}
                  >
                    {uploading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" /> Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4" /> Choose video file
                      </>
                    )}
                  </label>
                  <input
                    id="testimonial-video-upload"
                    data-testid="testimonial-video-upload-input"
                    type="file"
                    accept="video/*"
                    onChange={handleVideoUpload}
                    disabled={uploading}
                    className="hidden"
                  />
                </div>
                <p className="text-xs text-blue-600 mt-1">MP4, WebM, MOV. Max 10MB. For longer clips, paste a YouTube URL above.</p>
              </div>

              {formData.video_url && (
                <div className="mt-2 aspect-video bg-stone-900 rounded overflow-hidden">
                  {formData.video_url.startsWith('data:') ? (
                    <video
                      controls
                      className="w-full h-full object-contain bg-black"
                      preload="metadata"
                      data-testid="testimonial-video-preview"
                    >
                      <source src={formData.video_url} />
                    </video>
                  ) : (
                    <iframe
                      src={convertYouTubeUrl(formData.video_url)}
                      title="Video Preview"
                      className="w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  )}
                </div>
              )}

              {formData.video_url && (
                <button
                  type="button"
                  onClick={() => setFormData((prev) => ({ ...prev, video_url: '' }))}
                  data-testid="testimonial-video-clear"
                  className="text-xs text-red-600 hover:text-red-800 font-medium underline"
                >
                  Remove video
                </button>
              )}
            </div>
            <Input
              name="image_url"
              placeholder="Client Image URL (Optional - not needed if video is added)"
              value={formData.image_url}
              onChange={handleChange}
              data-testid="testimonial-image-input"
            />
            <div className="flex gap-3 pt-4">
              <Button
                type="submit"
                data-testid="testimonial-submit-button"
                className="flex-1 bg-emerald-900 hover:bg-emerald-800"
              >
                {editingTestimonial ? 'UPDATE TESTIMONIAL' : 'ADD TESTIMONIAL'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={closeModal}
                data-testid="testimonial-cancel-button"
              >
                CANCEL
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminTestimonials;